// Events tab: mirrors ClubDetailsScreen loadEventsData (:468-510) + handleToggleClubEventRsvp (:610-720).
// RSVP goes through hooks/useRsvpToggle and attendance through DataContext (cross-check F3/F6).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventService } from "../../../../services/eventService";
import { useData } from "../../context/DataContext";
import useRsvpToggle from "../../hooks/useRsvpToggle";
import { selectVisibleEvents } from "../../lib/format";
import { eventPath } from "../../lib/paths";
import { useToast } from "../Toast";
import { Button, Empty, SkeletonRows } from "../primitives";
import AttendanceNotice from "./AttendanceNotice";
import ClubEventCard from "./ClubEventCard";
import { msg } from "./clubUtils";

export default function ClubEvents({ club, isMember, resolved, payloadEvents }) {
  const toast = useToast();
  const clubId = club.id;
  const { goingEventIds, checkedInEventIds, userEventsLoaded } = useData();
  const [fetched, setFetched] = useState(null); // getEvents fallback result
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({}); // eventId -> optimistic / reconciled attendee_count
  const [tick, setTick] = useState(0);
  const gen = useRef({});

  // Events only when the club payload carried none. Attendance comes from DataContext (F3).
  useEffect(() => {
    let alive = true;
    const needFetch = !(resolved && payloadEvents);
    if (!needFetch) { setLoading(false); setError(null); return undefined; }
    (async () => {
      setLoading(true);
      setError(null);
      const ev = await EventService.getEvents({ clubId, futureOnly: true });
      if (!alive) return;
      if (ev.error) setError(msg(ev.error, "Could not load events."));
      else setFetched(ev.data || []);
      setLoading(false);
    })();
    return () => { alive = false; };
    // Re-run only when the club or the payload-vs-fetch decision changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, tick, resolved && !!payloadEvents]);

  const events = useMemo(() => {
    const base = resolved && payloadEvents
      ? selectVisibleEvents(payloadEvents, { futureOnly: true, canSeePrivate: () => isMember })
      : fetched || [];
    return base.map((e) => (counts[e.id] != null ? { ...e, attendee_count: counts[e.id] } : e));
  }, [resolved, payloadEvents, fetched, isMember, counts]);

  const eventsRef = useRef(events);
  eventsRef.current = events;
  const onCount = useCallback((eventId, delta) => {
    setCounts((p) => {
      const base = p[eventId] ?? eventsRef.current.find((e) => e.id === eventId)?.attendee_count ?? 0;
      return { ...p, [eventId]: Math.max(0, base + delta) };
    });
  }, []);
  // The one web RSVP write path: signedInAs(uid) before the write and before the result applies.
  const { toggle, isBusy } = useRsvpToggle({ onCount });

  const reconcile = useCallback(async (eventId) => {
    const g = (gen.current[eventId] = (gen.current[eventId] || 0) + 1);
    const { data, error: err } = await EventService.getEventById(eventId);
    if (g !== gen.current[eventId]) return;
    if (!err && data && typeof data.attendee_count === "number") setCounts((p) => ({ ...p, [eventId]: data.attendee_count }));
  }, []);

  const onToggle = async (event) => {
    const res = await toggle(event);
    if (!res?.ok) return;
    if (res.going) toast(`You're going to ${event.title}. See you there!`);
    reconcile(event.id);
  };

  if (loading) return <SkeletonRows rows={3} />;
  if (error) return <Empty icon="alert" title="Couldn't load events"><Button onClick={() => setTick((t) => t + 1)} data-testid="clubDetails.events.retry">Retry</Button></Empty>;
  if (events.length === 0) return <Empty>No upcoming events yet.</Empty>;
  return (
    <div className="col">
      <AttendanceNotice testId="clubDetails.attendance" />
      {events.map((e) => (
        <ClubEventCard
          key={e.id}
          event={e}
          club={club}
          to={eventPath(e.id)}
          hideRsvp={!userEventsLoaded}
          going={goingEventIds.has(e.id)}
          checkedIn={checkedInEventIds.has(e.id)}
          busy={isBusy(e.id)}
          onToggle={onToggle}
          testId={`clubDetails.event.${e.id}`}
        />
      ))}
    </div>
  );
}
